import React, { useState, useRef, useEffect } from 'react';
import { DispenseStatementFilterOption, DispenseStatementPageLabels, PillPackComFilterOption } from "../component";
import './DispenseStatement.scss';
import PillPackComMigrationBanner from "./PillPackComMigrationBanner";

interface DispenseStatementFilterOptionsDropdownProps {
    labels: DispenseStatementPageLabels;
    filterOptions: DispenseStatementFilterOption[];
    pillPackComFilterOptions?: PillPackComFilterOption[];
    pillPackComMigrationStatus?: string;
    onDownloadClick(filterOption: string | undefined): void;
    alertMessage?: string;
    hideFilterOption?: string;
    showChooseYear?: boolean;
}

/**
 * Dropdown-based year selector for downloading dispense statements.
 *
 * Renders up to two pui-dropdown-two components:
 *  - Amazon Pharmacy (always shown)
 *  - PillPack.com (shown only when migration is COMPLETE and options exist)
 *
 * Only one dropdown can have an active selection at a time. The AP dropdown
 * defaults to the most recent year (first option). When the user picks from
 * one dropdown, the other resets to the "select year" placeholder text.
 *
 * Selected values are prefixed with "pharmacy-" or "pillpack-" so the parent
 * can route the download request to the correct API endpoint.
 */
function DispenseStatementFilterOptionsDropdown(props: DispenseStatementFilterOptionsDropdownProps) {
    // Filter out any hidden option (used when re-rendering after a NotFoundError)
    const filteredApOptions = props.filterOptions.filter(
        opt => !props.hideFilterOption || opt.value !== props.hideFilterOption
    );

    // Default to the first (most recent) AP year so the dropdown isn't empty on load
    const defaultApValue = filteredApOptions[0] ? `pharmacy-${filteredApOptions[0].value}` : null;

    // Placeholder text shown on the unselected dropdown
    const selectYearText = props.labels.selectYearDropdown;

    // Only one of these should be non-null at a time — they're mutually exclusive
    const [apSelectedValue, setApSelectedValue] = useState<string | null>(defaultApValue);
    const [ppSelectedValue, setPpSelectedValue] = useState<string | null>(null);
    const apDropdownRef = useRef<any>(null);
    const ppDropdownRef = useRef<any>(null);

    const hasPillPack = props.pillPackComMigrationStatus === "COMPLETE"
        && props.pillPackComFilterOptions && props.pillPackComFilterOptions.length > 0;

    // Initialize AP dropdown: pre-select the first option and wire up selection handler
    useEffect(() => {
        const apDropdown = apDropdownRef.current;
        if (apDropdown) {
            if (filteredApOptions[0]) {
                apDropdown.value = filteredApOptions[0].value;
                apDropdown.selectedText = filteredApOptions[0].label;
            }
            apDropdown.onSelect = (value: string) => {
                setApSelectedValue(`pharmacy-${value}`);
                setPpSelectedValue(null);
                // Reset PP dropdown to placeholder when AP is selected
                if (ppDropdownRef.current) {
                    ppDropdownRef.current.value = null;
                    ppDropdownRef.current.selectedText = selectYearText;
                }
            };
        }
    }, []);

    // Initialize PP dropdown: set placeholder text and wire up selection handler
    useEffect(() => {
        const ppDropdown = ppDropdownRef.current;
        if (ppDropdown && hasPillPack) {
            ppDropdown.selectedText = selectYearText;
            ppDropdown.onSelect = (value: string) => {
                setPpSelectedValue(`pillpack-${value}`);
                setApSelectedValue(null);
                // Reset AP dropdown to placeholder when PP is selected
                if (apDropdownRef.current) {
                    apDropdownRef.current.value = null;
                    apDropdownRef.current.selectedText = selectYearText;
                }
            };
        }
    }, [hasPillPack]);

    const handleDownloadClick = () => {
        const selectedValue = apSelectedValue || ppSelectedValue;
        props.onDownloadClick(selectedValue || undefined);
    };

    const renderAlert = () => (
        <pui-box theme="error" style={{ width: "auto", marginTop: "24px" }}>
            <pui-stack direction="horizontal">
                <pui-stack-item>
                    <pui-icon imgClass="status-error-icon" spacingRight="small" />
                </pui-stack-item>
                <pui-stack-item overflow="true">
                    <pui-text input={props.alertMessage} />
                </pui-stack-item>
            </pui-stack>
        </pui-box>
    );

    // Download button is enabled only when one of the dropdowns has a selection
    const currentSelection = apSelectedValue || ppSelectedValue;

    return (
        <pui-section flowdirection="vertical" style={{ gap: "24px" }}>
            <pui-section-column>
                {props.showChooseYear && <pui-text input={props.labels.chooseYear} />}
                {props.alertMessage && renderAlert()}
            </pui-section-column>

            <pui-section-column class="dispense-statement__filter-options-list">
                {/* Amazon Pharmacy year selector */}
                <pui-dropdown-two
                    ref={apDropdownRef}
                    label={props.labels.amazonPharmacy}
                    enableScrollbar
                    style={{
                        width: '100%',
                        marginBottom: '16px',
                    }}
                >
                    {filteredApOptions.map(option => (
                        <pui-dropdown-item-two
                            key={`pharmacy-${option.value}`}
                            type="option"
                            label={option.label}
                            value={option.value}
                        />
                    ))}
                </pui-dropdown-two>

                {/* Shown while PillPack statement migration is still running */}
                {props.pillPackComMigrationStatus === "IN_PROGRESS" && (
                    <div style={{ marginTop: "16px" }}>
                        <pui-text
                            style={{ marginBottom: "13px" }}
                            input={props.labels.pillPackCom}
                            fontWeight="bold"
                        />
                        <PillPackComMigrationBanner
                            pillPackComStatementsInProgress={props.labels.pillPackComStatementsInProgress}
                            pillPackComCheckBackLater={props.labels.pillPackComCheckBackLater}
                        />
                    </div>
                )}

                {/* PillPack.com year selector — only when migration is complete */}
                {hasPillPack && (
                    <pui-dropdown-two
                        ref={ppDropdownRef}
                        label={props.labels.pillPackCom}
                        enableScrollbar
                        style={{
                            width: '100%',
                        }}
                    >
                        {props.pillPackComFilterOptions!.map(option => (
                            <pui-dropdown-item-two
                                key={`pillpack-${option.value}`}
                                type="option"
                                label={option.label}
                                value={option.value}
                            />
                        ))}
                    </pui-dropdown-two>
                )}
            </pui-section-column>

            <pui-section-column>
                <pui-button
                    id="button-to-download-dispense-statement"
                    data-csa-c-type="button"
                    data-csa-c-action="download"
                    data-csa-c-content-id="button-to-download-dispense-statement"
                    data-csa-c-slot-id="dispense-statement"
                    label={props.labels.download}
                    spacingTop="none"
                    textColor="black-color"
                    textSize="extra-large"
                    disabled={currentSelection === null || undefined}
                    onClick={handleDownloadClick}
                    icon="dispense-statement__download-icon"
                />
            </pui-section-column>
        </pui-section>
    );
}

export default DispenseStatementFilterOptionsDropdown;
